import React, { useEffect, useMemo, useState } from 'react';
import { RemainingCreditReportSortColumnId } from 'anchor-scout/types';
import { Link, useHistory } from 'react-router-dom';
import { Button, DropdownMenu, FilterPickerGroup, MultiSelectFilterPicker, Page, PageContentHeader, Spinner, TableList, TextButton, Typography } from 'seagull';
import { routes } from "../../../constants";
import { useStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import styles from "./RemainingCredit.module.scss";
import compact from 'lodash/compact';
import { exportCsv } from "../../../utils/csv.utils";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
const title = 'Remaining Credit Report';
function RemainingCreditRow(props) {
  const {
    data
  } = props;
  const items = compact([{
    value: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
      as: Link,
      to: routes.agreement(data.relationshipId),
      "data-testid": "remaining-credit-report-row-customer",
      variant: "paragraph1",
      noWrap: true,
      ellipsis: true
    }, data.customerName))
  }, {
    value: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "remaining-credit-report-row-agreement",
      variant: "paragraph1",
      noWrap: true,
      ellipsis: true
    }, data.agreementName))
  }, {
    value: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "remaining-credit-report-row-service",
      variant: "paragraph1",
      noWrap: true,
      ellipsis: true
    }, data.serviceName)
  }, {
    value: /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "remaining-credit-report-row-rate",
      variant: "paragraph1",
      noWrap: true
    }, data.credit)
  }]);
  return /*#__PURE__*/React.createElement(TableList.Row, {
    items: items,
    rowId: props.index,
    "data-testid": "remaining-credit-report-row-".concat(props.index),
    "data-heapid": "remaining-credit-report-row"
  });
}
const remainingCreditReportHeaderColumnDefsForExport = [{
  value: 'Company name'
}, {
  value: 'Agreement name'
}, {
  value: 'Service name'
}, {
  value: 'Credit'
}];
const headerItems = [{
  value: 'Company name',
  sortId: RemainingCreditReportSortColumnId.CompanyName
}, {
  value: 'Agreement name',
  sortId: RemainingCreditReportSortColumnId.AgreementName
}, {
  value: 'Service name',
  sortId: RemainingCreditReportSortColumnId.ServiceName
}, {
  value: 'Credit',
  sortId: RemainingCreditReportSortColumnId.Amount
}];
const sortIds = headerItems.map(item => item.sortId);
const defaultSort = {
  colIndex: sortIds.indexOf(RemainingCreditReportSortColumnId.CompanyName),
  sort: 'desc'
};
const remainingCreditReportColumnDefs = [{
  width: 234,
  sortable: true
}, {
  width: 234,
  sortable: true
}, {
  width: 234,
  sortable: true
}, {
  width: 148,
  sortable: true,
  hAlign: 'end'
}];
var FilterType = /*#__PURE__*/function (FilterType) {
  FilterType["CustomerName"] = "company";
  FilterType["AgreementName"] = "agreement";
  FilterType["ServiceName"] = "service";
  return FilterType;
}(FilterType || {});
const filterLabels = {
  [FilterType.CustomerName]: 'Company name',
  [FilterType.AgreementName]: 'Agreement name',
  [FilterType.ServiceName]: 'Service name'
};
export function FilterSelector(props) {
  const {
    selected,
    onSelect
  } = props;
  const items = useMemo(() => {
    const getEntry = (type, text) => ({
      type,
      text,
      disabled: selected.includes(type)
    });
    const items = Object.values(FilterType).map(key => {
      //const type = FilterType[key as keyof typeof FilterType];
      return getEntry(key, filterLabels[key]);
    });
    return items;
  }, [selected]);
  function getMenuItem(type, text, disabled) {
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      key: type,
      skin: "primary",
      "data-testid": "remaining-credit-report-filter-picker-".concat(type),
      disabled: disabled,
      onClick: () => onSelect(type)
    }, text);
  }
  return /*#__PURE__*/React.createElement(DropdownMenu, {
    "data-testid": "remaining-credit-report-filter-picker",
    hAlign: "center",
    target: /*#__PURE__*/React.createElement(TextButton, {
      "data-testid": "remaining-credit-report-add-filter",
      underline: "hover"
    }, "+Add filter")
  }, items.map(_ref => {
    let {
      type,
      text,
      disabled
    } = _ref;
    return getMenuItem(type, text, disabled);
  }));
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type, component) {
  return {
    component,
    hasValue(v) {
      return v.length > 0;
    },
    getEmptyValue() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [];
    },
    serialize(data) {
      return {
        [type]: data
      };
    }
  };
}
function StringListFilter(label, filterOptions) {
  return function CustomerNameFilterComponent(props) {
    return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
      withSearchbox: true,
      removable: true,
      startOpen: props.startOpen,
      "data-testid": "remaining-credit-report-".concat(props.type, "-filter"),
      value: props.value,
      options: filterOptions.map(option => ({
        value: option,
        label: option
      })),
      label: label,
      onChange: props.onChange,
      onRemove: props.onRemove
    });
  };
}
export const makeFiltersConfig = (serviceNames, customerNames, agreementNames) => ({
  [FilterType.CustomerName]: getSimpleMultiSelectFilterConfig(FilterType.CustomerName, StringListFilter(filterLabels[FilterType.CustomerName], customerNames)),
  [FilterType.ServiceName]: getSimpleMultiSelectFilterConfig(FilterType.ServiceName, StringListFilter(filterLabels[FilterType.ServiceName], serviceNames)),
  [FilterType.AgreementName]: getSimpleMultiSelectFilterConfig(FilterType.AgreementName, StringListFilter(filterLabels[FilterType.AgreementName], agreementNames))
});
export function RemainingCreditReport() {
  const businessId = useStore(selectors.user.businessId);
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [availableAgreements, setAvailableAgreements] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const sortStringified = sort && JSON.stringify(sort);
  const filtersConfig = useMemo(() => makeFiltersConfig(availableServices, availableCustomers, availableAgreements), [availableServices, availableCustomers, availableAgreements]);
  const setters = {
    [FilterType.CustomerName]: setFilteredCustomers,
    [FilterType.ServiceName]: setFilteredServices,
    [FilterType.AgreementName]: setFilteredAgreements
  };
  const values = {
    [FilterType.CustomerName]: filteredCustomers,
    [FilterType.ServiceName]: filteredServices,
    [FilterType.AgreementName]: filteredAgreements
  };
  function addFilter(type) {
    setSelectedFilters(selectedFilters.concat(type));
  }
  function removeFilter(type) {
    setSelectedFilters(selectedFilters.filter(t => t !== type));
    switch (type) {
      case FilterType.CustomerName:
        setFilteredCustomers([]);
        break;
      case FilterType.ServiceName:
        setFilteredServices([]);
        break;
      case FilterType.AgreementName:
        setFilteredAgreements([]);
        break;
    }
  }
  useEffect(() => {
    document.title = title;
  }, []);
  useEffect(() => {
    if (businessId == null) return;
    api.reports.remainingCreditReportServices({
      businessId
    }).then(setAvailableServices);
    api.reports.remainingCreditReportCustomers({
      businessId
    }).then(setAvailableCustomers);
    api.reports.remainingCreditReportAgreements({
      businessId
    }).then(setAvailableAgreements);
  }, [businessId]);
  useEffect(() => {
    if (businessId == null) return;
    setIsRefreshing(true);
    api.reports.remainingCreditReport({
      businessId,
      customers: filteredCustomers,
      services: filteredServices,
      agreements: filteredAgreements,
      sort: sort ? [sortIds[sort.colIndex], sort.sort] : undefined
    }).then(data => {
      setReportData(data);
      setIsRefreshing(false);
    });
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, [businessId, filteredServices, filteredCustomers, filteredAgreements, sortStringified]);
  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "remaining-credit-report",
    header: "Remaining Credit Report",
    variant: "containedHeight",
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      skin: "primary",
      "data-testid": "export-report-btn",
      onClick: () => exportCsv(makeCsv(remainingCreditReportHeaderColumnDefsForExport.map(_ref2 => {
        let {
          value
        } = _ref2;
        return value;
      }), reportData), csvFileName)
    }, "Export"))
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: remainingCreditReportColumnDefs,
    numRows: reportData.length,
    sort: sort,
    onSortChange: changeSort
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBlockEnd: '24px'
    }
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    flexWrap: true,
    "data-testid": "remaining-credit-report-filters"
  }, selectedFilters.map(type => renderFilterComponent(type, filtersConfig, selectedFilters, values[type], setters[type], removeFilter)), selectedFilters.length < Object.keys(FilterType).length && /*#__PURE__*/React.createElement(FilterSelector, {
    selected: selectedFilters,
    onSelect: addFilter
  })))), /*#__PURE__*/React.createElement(TableList.Structure, {
    header: /*#__PURE__*/React.createElement(TableList.Header, {
      stickyToTop: true,
      items: headerItems,
      "data-testid": "remaining-credit-report-table-header"
    }),
    content: isRefreshing ? /*#__PURE__*/React.createElement("div", {
      className: styles.loading,
      "data-testid": "remaining-credit-table-loading"
    }, /*#__PURE__*/React.createElement(Spinner, {
      centeredInBox: true
    })) : reportData.length ? reportData.map((reportRow, index) => /*#__PURE__*/React.createElement(RemainingCreditRow, {
      key: "".concat(reportRow.customerName, "-").concat(reportRow.serviceName, "-").concat(index),
      index: index,
      data: reportRow
    })) : null
  })));
}
function renderFilterComponent(type, filtersConfig, selectedFilters, value, onChange, onRemoveFilter) {
  return /*#__PURE__*/React.createElement(filtersConfig[type].component, {
    type,
    key: type,
    title: filterLabels[type],
    startOpen: selectedFilters.includes(type),
    value,
    onChange,
    onRemove: () => onRemoveFilter(type)
  });
}
const csvFileName = 'remaining-credit-report.csv';
function makeCsv(headerRow, entries) {
  return [headerRow].concat(entries.map(entry => [entry.customerName, entry.agreementName, entry.serviceName, entry.rawCredit.toString()]));
}